import { useQuery } from '@tanstack/react-query';
import type {
  SettlementItem,
  SettlementItemSourceEnum,
} from '@/lib/api-admin/types';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';

export interface SettlementItemsQueryParams {
  runId: string;
  page?: number | undefined;
  size?: number | undefined;
  sort?: string | undefined;
  sourceType?: ReadonlyArray<SettlementItemSourceEnum> | undefined;
  isRefund?: boolean | undefined;
}

export interface SettlementItemsPage {
  items: ReadonlyArray<SettlementItem>;
  meta: ApiMeta;
}

interface AdminQuery {
  page?: number;
  size?: number;
  sort?: string;
  sourceType?: SettlementItemSourceEnum[];
  isRefund?: boolean;
}

const cleanQuery = (p: SettlementItemsQueryParams): AdminQuery => {
  const out: AdminQuery = {};
  out.page = p.page != null ? Math.max(0, p.page - 1) : 0;
  if (p.size) out.size = p.size;
  if (p.sort) out.sort = p.sort;
  if (p.sourceType && p.sourceType.length > 0) out.sourceType = [...p.sourceType];
  if (p.isRefund !== undefined) out.isRefund = p.isRefund;
  return out;
};

export const settlementItemsQueryKey = (p: SettlementItemsQueryParams) =>
  ['admin', 'settlement-items', p.runId, p] as const;

export const useSettlementItemsQuery = (p: SettlementItemsQueryParams) =>
  useQuery<SettlementItemsPage>({
    queryKey: settlementItemsQueryKey(p),
    enabled: Boolean(p.runId),
    queryFn: async () => {
      const [data, meta] = await apiClient.request(
        'get',
        '/admin/v1/settlements/{runId}/items',
        { params: { path: { runId: p.runId }, query: cleanQuery(p) } },
      );
      const payload = data as { items: SettlementItem[] };
      return { items: payload.items, meta };
    },
  });
